/**
 * Pill centrada para eventos del timeline (asignaciones, cambios de tag, etc.).
 * Patrón Chatwoot: texto gris, fondo subdued, padding compacto.
 */

import { Box, InlineStack } from '@/lib/polaris';
import type { WaAuditEvent } from '@/hooks/useWaAuditEvents';

interface Props {
  event: WaAuditEvent;
  /** Resuelve actor_id → nombre legible. */
  resolveName: (userId: string | null | undefined) => string;
}

/** Traduce un evento de audit_trail a un texto humano. */
function describe(event: WaAuditEvent, resolveName: Props['resolveName']): string | null {
  const actor = event.actor_id ? resolveName(event.actor_id) : 'Sistema';
  const d = event.details || {};
  const get = (k: string): string | null => {
    const v = d[k];
    return typeof v === 'string' ? v : null;
  };

  switch (event.action) {
    case 'assign': {
      const to = get('to_agent_name') || (get('to_agent_id') ? resolveName(get('to_agent_id')) : null);
      if (!to) return `${actor} asignó la conversación`;
      if (event.actor_id && get('to_agent_id') === event.actor_id) return `${actor} auto-asignado a esta conversación`;
      return `Asignado a ${to} por ${actor}`;
    }
    case 'unassign': {
      const from = get('from_agent_name') || (get('from_agent_id') ? resolveName(get('from_agent_id')) : null);
      return from ? `Desasignado de ${from} por ${actor}` : `${actor} desasignó la conversación`;
    }
    case 'transfer': {
      const to = get('to_agent_name') || (get('to_agent_id') ? resolveName(get('to_agent_id')) : null);
      return to ? `Transferido a ${to} por ${actor}` : `${actor} transfirió la conversación`;
    }
    case 'team_assign': {
      const to = get('to_team_name') ?? get('to_team_id');
      return to ? `Equipo cambiado a ${to} por ${actor}` : `${actor} asignó un equipo`;
    }
    case 'team_unassign': {
      const from = get('from_team_name');
      return from ? `Equipo ${from} removido por ${actor}` : `${actor} removió el equipo`;
    }
    case 'priority_change': {
      const newP = get('to_priority');
      const PRIORITY_LABEL: Record<string, string> = {
        urgent: 'Urgente', high: 'Alta', medium: 'Media', low: 'Baja',
      };
      if (!newP) return `${actor} quitó la prioridad`;
      return `Prioridad cambiada a ${PRIORITY_LABEL[newP] ?? newP} por ${actor}`;
    }
    case 'resolve':
      return `${actor} marcó como resuelta`;
    case 'reopen': {
      if (!event.actor_id) {
        const trigger = get('trigger');
        if (trigger === 'customer_reply') return 'Reabierta automáticamente al recibir respuesta del cliente';
        if (trigger === 'snooze_expired') return 'Reabierta automáticamente porque venció la posposición';
        return 'Conversación reabierta';
      }
      return `${actor} reabrió la conversación`;
    }
    case 'snooze': {
      const until = get('until');
      if (!until) return `${actor} pospuso la conversación`;
      const when = new Date(until);
      const fmt = when.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
      return `${actor} pospuso la conversación hasta ${fmt}`;
    }
    case 'unsnooze':
      return `${actor} quitó la posposición`;
    case 'block':
      return `${actor} bloqueó el contacto`;
    case 'unblock':
      return `${actor} desbloqueó el contacto`;
    case 'tag_add': {
      const tag = get('tag');
      return tag ? `${actor} agregó ${tag}` : `${actor} agregó una etiqueta`;
    }
    case 'tag_remove': {
      const tag = get('tag');
      return tag ? `${actor} eliminó ${tag}` : `${actor} eliminó una etiqueta`;
    }
    case 'mute':
      return `${actor} silenció la conversación`;
    case 'unmute':
      return `${actor} reactivó las notificaciones`;
    case 'ai_toggle': {
      const enabled = d.enabled === true;
      return `${actor} ${enabled ? 'activó' : 'desactivó'} la IA`;
    }
    case 'note_add':
      return `${actor} agregó una nota interna`;
    case 'forward':
      return `${actor} reenvió un mensaje`;
    case 'message_delete':
      return `${actor} eliminó un mensaje`;
    default:
      return null;
  }
}

export function WaTimelinePill({ event, resolveName }: Props) {
  const text = describe(event, resolveName);
  if (!text) return null;
  return (
    <Box paddingBlock="150">
      <InlineStack align="center">
        {/* Pill custom — el `borderRadius: 12` + `fontSize: 11` + maxWidth 80%
            son intencionales (estilo WhatsApp Web timeline). Polaris Badge
            usa otro tamaño/forma; lo mantenemos custom. */}
        <span
          style={{
            fontSize: 11,
            padding: '3px 10px',
            borderRadius: 12,
            background: 'var(--p-color-bg-surface-secondary)',
            color: 'var(--p-color-text-subdued)',
            border: '1px solid var(--p-color-border-subdued)',
            maxWidth: '80%',
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          {text}
        </span>
      </InlineStack>
    </Box>
  );
}
